import { listen, put } from '@fcanvas/communicate';
import { onBeforeUnmount, onMounted } from 'vue';
import type { BrandingPreviewApi, BrandingPreviewState } from '../utils/branding-preview-channel';
import { isBrandingPreviewPath } from '../utils/branding-preview-channel';

/**
 * Child half of the branding preview channel, used by `layouts/Default.vue`.
 *
 * `/pending-payment/branding-preview` is the real page, so it still reads its branding from the
 * session — which holds the *saved* values. The modals preview *unsaved* ones, so the frame asks
 * the host for them as soon as it is mounted and keeps listening for every subsequent edit.
 *
 * Outside the preview path this is a no-op, so the layout can call it unconditionally. Opening the
 * page in a normal tab (`window.parent === window`) simply never gets an answer, and the session
 * branding is kept — that is what makes the URL usable on its own.
 */
export function useBrandingPreview(apply: (state: BrandingPreviewState) => void): boolean {
  const route = useRoute();

  if (!isBrandingPreviewPath(route.path)) {
    return false;
  }

  // Registered during setup, i.e. strictly before the host mounts and before our own `onMounted`
  // handshake below, so neither side can miss the other.
  const stopListening = listen<BrandingPreviewApi>(window, 'branding-preview:apply', apply);

  onMounted(() => {
    put<BrandingPreviewApi>(window.parent, {
      name: 'branding-preview:ready',
      targetOrigin: window.location.origin,
    })
      .then(apply)
      // No host listening (opened directly): keep whatever the session says.
      .catch(() => undefined);
  });

  onBeforeUnmount(stopListening);

  return true;
}
